import type { GenericAvailabilityType } from '../cards'
import { ProductLifeCycleState } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'

import classNames from 'classnames'
import AvailabilitySvgIcon from '@dustin-web/microsite-components/src/icon/generated/availability'
import DeliverySvgIcon from '@dustin-web/microsite-components/src/icon/generated/delivery-2'
import ErrorBoundary from '@dustin-web/microsite-components/src/utils/error-boundary'

const AvailabilityIcon = ({ color, className }: { color: string; className?: string }) => (
  <AvailabilitySvgIcon
    className={`c-availability__icon c-availability__icon--${color} ${className}`}
    multiLayered
  />
)

const bannedLifeCycleStatesForPage: ProductLifeCycleState[] = [
  ProductLifeCycleState.EndOfLife,
  ProductLifeCycleState.TempStopped,
  ProductLifeCycleState.NotReleased,
]

export type AvailabilityText = {
  isEstimatedShippingDate?: boolean
  color: string
  stockInfoText: string
  deliveryInfoText: string
  bidInfo: string
  tooltipText: string
}
export type Props = {
  availability?: GenericAvailabilityType | null
  isBid?: boolean
  listForm?: boolean
  className?: string
  iconClassName?: string
  showOnlyIcon?: boolean
  availabilityTextClassName?: boolean
  defaultContainerClassName?: string
  containerClassNames?: string
  deliveryContainerClassName?: string
  showIsBidInfoText?: boolean
  text: AvailabilityText
  compactStatus?: boolean
}

export const ProductAvailabilityTest = (props: Props) => {
  return (
    <ErrorBoundary>
      <ProductAvailabilityInternal {...props} />
    </ErrorBoundary>
  )
}

const ProductAvailabilityInternal = ({
  availability,
  isBid = false,
  listForm = false,
  className,
  iconClassName,
  availabilityTextClassName = false,
  showOnlyIcon = false,
  defaultContainerClassName,
  text,
  containerClassNames,
  deliveryContainerClassName,
  showIsBidInfoText,
  compactStatus,
}: Props) => {
  if (
    !availability ||
    !availability.productLifeCycleState ||
    bannedLifeCycleStatesForPage.includes(availability.productLifeCycleState)
  ) {
    return null
  }
  const { isEstimatedShippingDate, deliveryInfoText, color, stockInfoText, bidInfo } = text
  if (listForm || isEstimatedShippingDate) {
    return (
      <ListOrEstimatedShippingDate
        deliveryInfoText={deliveryInfoText}
        color={color}
        iconClassName={iconClassName}
        stockText={stockInfoText}
        className={className}
        showOnlyIcon={showOnlyIcon}
        defaultContainerClassName={defaultContainerClassName}
        containerClassNames={classNames(containerClassNames, { 'u-pt-8': listForm })}
        compactStatus={compactStatus}
      />
    )
  }

  return (
    <Default
      className={className}
      color={color}
      iconClassName={iconClassName}
      useAvailabilityStockTextClassName={availabilityTextClassName}
      stockText={stockInfoText}
      deliveryInfoText={deliveryInfoText}
      isBid={isBid}
      infoText={bidInfo}
      defaultContainerClassName={defaultContainerClassName}
      containerClassNames={containerClassNames}
      deliveryContainerClassName={deliveryContainerClassName}
      showIsBidInfoText={showIsBidInfoText}
    />
  )
}

type ListOrEstimatedShippingDateProps = {
  color: string
  iconClassName?: string
  stockText: string
  className?: string
  showOnlyIcon?: boolean
  defaultContainerClassName?: string
  stockTextClassName?: string
  deliveryInfoText?: string
  containerClassNames?: string
  compactStatus?: boolean
}
const ListOrEstimatedShippingDate = ({
  color,
  containerClassNames,
  stockText,
  className = 'd-flex flex-column gap-4',
  showOnlyIcon,
  stockTextClassName = 'c-availability__text',
  deliveryInfoText,
  iconClassName,
  compactStatus,
}: ListOrEstimatedShippingDateProps) => (
  <>
    {compactStatus ? (
      <div className={classNames('d-flex', containerClassNames)}>
        <div className={classNames(className)}>
          <span className="d-flex u-fs-12 gap-4 align-items-center">
            <AvailabilityIcon color={color} className={classNames('u-mr-0', iconClassName)} />
            {!showOnlyIcon && (
              <span className={stockTextClassName}>{`${stockText}. ${deliveryInfoText}`}</span>
            )}
          </span>
        </div>
      </div>
    ) : (
      <div className={classNames('d-flex', containerClassNames)}>
        <div className={classNames(className)}>
          <span className="d-flex u-fs-12 gap-4 align-items-center">
            <AvailabilityIcon color={color} className={classNames('u-mr-0', iconClassName)} />
            {!showOnlyIcon && <span className={stockTextClassName}>{stockText}</span>}
          </span>
          <span className="d-flex u-fs-12 gap-4 align-items-center">
            <DeliverySvgIcon className={classNames('i-16', iconClassName)} />
            <span>{deliveryInfoText}</span>
          </span>
        </div>
      </div>
    )}
  </>
)
type DefaultProps = {
  className?: string
  defaultContainerClassName?: string
  stockClassName?: string
  infoTextClassName?: string
  color: string
  iconClassName?: string
  useAvailabilityStockTextClassName?: boolean
  stockText: string
  isBid: boolean
  infoText: string
  deliveryInfoText: string
  containerClassNames?: string
  deliveryContainerClassName?: string
  showIsBidInfoText?: boolean
}
const Default = ({
  className = 'd-flex gap-4',
  containerClassNames,
  defaultContainerClassName = 'c-availability',
  stockClassName = 'c-availability__text',
  infoTextClassName = 'u-py-8',
  color,
  useAvailabilityStockTextClassName,
  stockText,
  infoText,
  deliveryInfoText,
  iconClassName,
  deliveryContainerClassName = 'd-flex gap-4',
  showIsBidInfoText,
}: DefaultProps) => (
  <div className={classNames('d-flex flex-column gap-4', containerClassNames)}>
    <div className={classNames(defaultContainerClassName, className)}>
      <AvailabilityIcon color={color} className={classNames('u-mr-0', iconClassName)} />
      <span
        className={classNames(stockClassName, {
          'c-availability__product-information-text': useAvailabilityStockTextClassName,
        })}>
        {stockText}
      </span>
    </div>
    <div className={classNames('d-flex align-items-center', deliveryContainerClassName)}>
      <DeliverySvgIcon className={classNames('i-16 u-mr-0', iconClassName)} />
      <span
        className={classNames(stockClassName, {
          'c-availability__product-information-text': useAvailabilityStockTextClassName,
        })}>
        {deliveryInfoText}
      </span>
    </div>
    {showIsBidInfoText && <div className={infoTextClassName}>{infoText}</div>}
  </div>
)
export default ProductAvailabilityTest
